import { ReactNode, useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Center,
  Group,
  Loader,
  Pagination,
  Popover,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconFilter, IconFilterFilled, IconX } from "@tabler/icons-react";

/**
 * DataTable — универсальная таблица с per-column фильтрами.
 * Требование ТЗ 01.07.2026 п.5: «сортировки как в фильтрах Excel во всех
 * сущностях, где это полезно» (заказчик показывал планфиксовский стиль).
 *
 * Фильтры работают на КЛИЕНТЕ по массиву `rows`. Это ок пока страница
 * умещается в page_size (обычно 50) — заказчику важнее быстрый сфокусированный
 * поиск, чем всеобъемлющий бэкенд-фильтр (последний уже покрыт через
 * top-level TextInput и селекты в конкретных страницах).
 */

export interface DataTableFilterConfig<T> {
  /** 'text' — свободный поиск (contains, case-insensitive).
   *  'select' — выпадашка с фиксированным списком. */
  type: "text" | "select";
  /** Извлечь значение поля из строки — используется и для отображения, и для сравнения. */
  getValue: (row: T) => string | number | null | undefined;
  /** Для type='select' — список опций. */
  options?: { value: string; label: string }[];
  /** Плейсхолдер поля. */
  placeholder?: string;
}

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: number | string;
  /** Если задано — над заголовком появится иконка фильтра. */
  filter?: DataTableFilterConfig<T>;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[] | undefined;
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
}

export function DataTable<T>({
  columns,
  rows,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  emptyMessage = "Нет данных",
  onRowClick,
  rowKey,
}: DataTableProps<T>) {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const setFilter = (key: string, value: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (!value) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  // Применяем фильтры к rows.
  const filtered = useMemo(() => {
    if (!rows) return rows;
    const activeKeys = Object.keys(columnFilters);
    if (activeKeys.length === 0) return rows;
    return rows.filter((r) => {
      for (const key of activeKeys) {
        const col = columns.find((c) => c.key === key);
        if (!col?.filter) continue;
        const raw = col.filter.getValue(r);
        const rowVal = raw == null ? "" : String(raw).toLowerCase();
        const needle = columnFilters[key].toLowerCase();
        if (col.filter.type === "select") {
          if (rowVal !== needle) return false;
        } else {
          if (!rowVal.includes(needle)) return false;
        }
      }
      return true;
    });
  }, [rows, columnFilters, columns]);

  const displayRows = filtered ?? [];
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const activeFilterCount = Object.keys(columnFilters).length;

  return (
    <Box>
      {activeFilterCount > 0 && (
        <Group gap="xs" mb="xs">
          <Text size="xs" c="dimmed">Активных фильтров: {activeFilterCount}</Text>
          <ActionIcon
            variant="subtle"
            size="xs"
            color="red"
            onClick={() => setColumnFilters({})}
            title="Сбросить все фильтры"
          >
            <IconX size={12} />
          </ActionIcon>
        </Group>
      )}

      <Box style={{ position: "relative", minHeight: 200 }}>
        {loading && (
          <Center
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.5)",
              zIndex: 1,
            }}
          >
            <Loader />
          </Center>
        )}
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              {columns.map((c) => (
                <Table.Th key={c.key} style={{ width: c.width }}>
                  <Group gap={4} wrap="nowrap" justify="space-between">
                    <span>{c.header}</span>
                    {c.filter && (
                      <ColumnFilterPopover
                        columnKey={c.key}
                        config={c.filter}
                        value={columnFilters[c.key] ?? ""}
                        onChange={(v) => setFilter(c.key, v)}
                      />
                    )}
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {displayRows.length > 0 ? (
              displayRows.map((r) => (
                <Table.Tr
                  key={rowKey(r)}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {columns.map((c) => (
                    <Table.Td key={c.key}>{c.render(r)}</Table.Td>
                  ))}
                </Table.Tr>
              ))
            ) : !loading ? (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Center py="md">
                    <Text c="dimmed">{emptyMessage}</Text>
                  </Center>
                </Table.Td>
              </Table.Tr>
            ) : null}
          </Table.Tbody>
        </Table>
      </Box>
      <Group justify="space-between" mt="md">
        <Text size="sm" c="dimmed">
          Всего: <strong>{total}</strong>
          {activeFilterCount > 0 && filtered && (
            <> · Отфильтровано: <strong>{filtered.length}</strong></>
          )}
          {" "}(стр. {page} из {totalPages})
        </Text>
        <Pagination
          value={page}
          onChange={onPageChange}
          total={totalPages}
          siblings={1}
          size="sm"
        />
      </Group>
    </Box>
  );
}

// ─── Внутренняя вспомогательная плашка с фильтром ───────────────────────

interface FilterPopoverProps<T> {
  columnKey: string;
  config: DataTableFilterConfig<T>;
  value: string;
  onChange: (v: string) => void;
}

function ColumnFilterPopover<T>({ config, value, onChange }: FilterPopoverProps<T>) {
  const [opened, setOpened] = useState(false);
  const isActive = value !== "";
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      withinPortal
      shadow="md"
    >
      <Popover.Target>
        <ActionIcon
          size="xs"
          variant={isActive ? "filled" : "subtle"}
          color={isActive ? "blue" : "gray"}
          onClick={(e) => {
            e.stopPropagation();
            setOpened((v) => !v);
          }}
          title="Фильтр по колонке"
        >
          {isActive ? <IconFilterFilled size={12} /> : <IconFilter size={12} />}
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p="xs" onClick={(e) => e.stopPropagation()}>
        <Stack gap="xs" style={{ minWidth: 200 }}>
          {config.type === "select" ? (
            <Select
              placeholder={config.placeholder ?? "Выберите значение"}
              data={config.options ?? []}
              value={value || null}
              onChange={(v) => onChange(v ?? "")}
              clearable
              searchable
            />
          ) : (
            <TextInput
              placeholder={config.placeholder ?? "Поиск"}
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              autoFocus
            />
          )}
          {isActive && (
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={() => { onChange(""); setOpened(false); }}
              title="Сбросить"
            >
              <IconX size={14} />
            </ActionIcon>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
